import { ChangeDetectionStrategy, Component, Input, OnChanges, ViewEncapsulation, inject, input } from '@angular/core';
import type { StoryblokRichTextNode, StoryblokRichTextNodeTypes } from '@storyblok/richtext';
import { DynamicComponent, DynamicIoDirective } from 'ng-dynamic-component';
import { StoryblokComponentInstance } from '../../../interfaces/storyblok';
import { SafeHtmlPipe } from '../../../pipes/safe-html.pipe';
import { StoryblokLoadComponentsService } from '../../../services/storyblok-load-components.service';
import { HtmlDirective } from '../../../shared-extra/directives/html.directive';
import { RouterLinkDirective } from '../../../shared-extra/directives/route.directive';
import { BlockStyle } from '../../interfaces/block-style';
import { StoryblokRichTextPipe } from '../../storyblok-rich-text.pipe';
import { BlockWrapperComponent } from '../block-wrapper/block-wrapper.component';

@Component({
  selector: 'app-text',
  templateUrl: './text.component.html',
  styleUrls: ['./text.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLinkDirective,
    BlockWrapperComponent,
    StoryblokRichTextPipe,
    DynamicComponent,
    DynamicIoDirective,
    SafeHtmlPipe,
    HtmlDirective,
  ],
})
export class TextComponent implements OnChanges {
  private storyblokLoadComponentsService = inject(StoryblokLoadComponentsService);
  @Input() _uid: string; // just here to prevent errors
  @Input() component: string; // just here to prevent errors
  @Input() inTextComponent: boolean; // just here to prevent errors

  @Input() _editable: string;
  @Input() style: BlockStyle[];

  @Input() content: StoryblokRichTextNode<string>;
  @Input() textSize: string;
  @Input() textStyle: string;

  @Input() mobileAlignment: string;
  @Input() tabletAlignment: string;
  @Input() desktopAlignment: string;

  @Input() ulIcons: string;
  @Input() ulIconColor: string;

  /** Drop pasted underline/text-color marks (used for blog articles). */
  readonly stripPastedMarks = input(false);

  /** Lock richtext headings to the mobile sizes in this file (skip 480/640 viewport bumps). */
  readonly mobileType = input(false);

  contents: (StoryblokRichTextNode<string> | { type: 'components'; body: StoryblokComponentInstance[] })[];

  /** Align from richtext first block textAlign. */
  effectiveAlignment: string = 'center';

  components = this.storyblokLoadComponentsService.components;

  ngOnChanges() {
    this.effectiveAlignment = this.resolveAlignment();

    this.contents = [];

    let content: StoryblokRichTextNode<string> = {
      type: 'doc' as StoryblokRichTextNodeTypes,
      content: [],
    };

    this.content.content.forEach(item => {
      if (item.type === 'blok') {
        this.contents.push(content);
        content = {
          type: 'doc' as StoryblokRichTextNodeTypes,
          content: [],
        };

        item.attrs.body.forEach(blok => {
          if (!('style' in blok)) return;
          if (!blok.style) return;
          if (Array.isArray(blok.style) && !blok.style.length) {
            blok.style = [{} as BlockStyle];
          }
          blok.style[0].inWrapper = true;
        });
        this.contents.push({
          type: 'components',
          body: item.attrs.body,
        });
      } else {
        content.content.push(item);
      }
    });
    this.contents.push(content);
  }

  private resolveAlignment(): string {
    const first = this.content?.content?.[0] as { attrs?: { textAlign?: string | null } } | undefined;
    const fromRich = first?.attrs?.textAlign;
    if (fromRich === 'left' || fromRich === 'center' || fromRich === 'right') return fromRich;
    return 'center';
  }

  dynamicInputs(values: { [key: string]: unknown }) {
    return { ...values, inTextComponent: true };
  }
}
